import { z } from 'zod';
import { schemaConfidentialite } from './documents';

const uuid = z.string().uuid('Identifiant invalide');
const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');

export const SENS_COURRIER = ['arrivee', 'depart'] as const;
export const CANAUX_COURRIER = ['papier', 'courriel', 'portail', 'main_propre', 'poste'] as const;
export const PRIORITES = ['normale', 'urgente'] as const;

export const ETATS_COURRIER_ARRIVEE = ['enregistre', 'affecte', 'en_traitement', 'repondu', 'classe', 'archive'] as const;
export const ETATS_COURRIER_DEPART = ['brouillon', 'en_validation', 'valide', 'signe', 'envoye', 'archive'] as const;
/** États d'un courrier arrivée considéré comme traité (COU-03). */
export const ETATS_COURRIER_TRAITES = ['repondu', 'classe', 'archive'] as const;

/** Délai interne de traitement par défaut d'un courrier arrivée affecté, en jours ouvrés. */
export const DELAI_TRAITEMENT_COURRIER_JOURS = 10;

export const schemaCreationCourrier = z
  .object({
    sens: z.enum(SENS_COURRIER),
    objet: z.string().trim().min(2).max(500),
    correspondant_nom: z.string().trim().min(1).max(300),
    correspondant_type: z.enum(['personne', 'organisme']).default('personne'),
    correspondant_adresse: z.string().trim().max(500).optional(),
    date_courrier: dateCivile.optional(),
    canal: z.enum(CANAUX_COURRIER),
    priorite: z.enum(PRIORITES).default('normale'),
    confidentialite: schemaConfidentialite.default('ordinaire'),
    acces_nominatif: z.array(uuid).max(50).default([]),
    service_affectataire_id: uuid.optional(),
    echeance_interne: dateCivile.optional(),
    dossier_id: uuid.optional(),
  })
  .refine((v) => v.confidentialite !== 'confidentiel' || v.acces_nominatif.length > 0, {
    message: 'Un courrier confidentiel exige une liste nominative',
    path: ['acces_nominatif'],
  });

export const schemaModificationCourrier = z.object({
  objet: z.string().trim().min(2).max(500).optional(),
  correspondant_nom: z.string().trim().min(1).max(300).optional(),
  correspondant_type: z.enum(['personne', 'organisme']).optional(),
  correspondant_adresse: z.string().trim().max(500).nullable().optional(),
  date_courrier: dateCivile.nullable().optional(),
  priorite: z.enum(PRIORITES).optional(),
  confidentialite: schemaConfidentialite.optional(),
  acces_nominatif: z.array(uuid).max(50).optional(),
  echeance_interne: dateCivile.nullable().optional(),
  dossier_id: uuid.nullable().optional(),
});

export const schemaAffectationCourrier = z.object({
  service_id: uuid,
  echeance_interne: dateCivile.optional(),
  motif: z.string().trim().max(1000).optional(),
});

export const schemaTransmissionCourrier = z.object({
  /** Service destinataire (courrier arrivée) ; absent pour un départ transmis en validation. */
  service_id: uuid.optional(),
  motif: z.string().trim().max(1000).optional(),
});

export const schemaReponseCourrier = z.object({
  objet: z.string().trim().min(2).max(500).optional(),
  canal: z.enum(CANAUX_COURRIER).default('papier'),
  confidentialite: schemaConfidentialite.optional(),
});

export const schemaActeCourrier = z.object({
  motif: z.string().trim().max(1000).optional(),
  delegation_id: uuid.optional(),
});

export const schemaEnvoiCourrier = z.object({
  canal: z.enum(CANAUX_COURRIER),
  preuve: z.string().trim().min(2).max(1000),
  date_envoi: dateCivile.optional(),
});

export const schemaClassementCourrier = z.object({
  motif: z.string().trim().max(1000).optional(),
  classement_id: uuid.optional(),
});

export const schemaFiltreCourriers = z.object({
  sens: z.enum(SENS_COURRIER).optional(),
  etat: z.string().regex(/^[a-z_]+$/).optional(),
  service: uuid.optional(),
  non_affectes: z.enum(['oui', 'non']).optional(),
  en_retard: z.enum(['oui', 'non']).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(50),
  apres: z.string().max(200).optional(),
});

export const schemaFiltreRegistre = z.object({
  sens: z.enum(SENS_COURRIER),
  annee: z.coerce.number().int().min(2000).max(2999),
});

// Réponses ---------------------------------------------------------------------------------

export const schemaCourrier = z.object({
  id: uuid,
  numero: z.string(),
  sens: z.enum(SENS_COURRIER),
  objet: z.string(),
  correspondant_nom: z.string(),
  correspondant_type: z.enum(['personne', 'organisme']),
  correspondant_adresse: z.string().nullable(),
  date_courrier: z.string().nullable(),
  date_enregistrement: z.string(),
  canal: z.enum(CANAUX_COURRIER),
  priorite: z.enum(PRIORITES),
  confidentialite: schemaConfidentialite,
  service_affectataire: z.object({ id: uuid, nom: z.string() }).nullable(),
  echeance_interne: z.string().nullable(),
  en_retard: z.boolean(),
  etat: z.string(),
  reponse_a_courrier_id: z.string().nullable(),
  reponse_a_numero: z.string().nullable(),
  dossier_id: z.string().nullable(),
  workflow_instance_id: z.string().nullable(),
  signe_le: z.string().nullable(),
  delegation_signature_id: z.string().nullable(),
  envoi: z.object({ canal: z.enum(CANAUX_COURRIER), preuve: z.string(), envoye_le: z.string() }).nullable(),
});
export type Courrier = z.infer<typeof schemaCourrier>;

export const schemaFicheCourrier = schemaCourrier.extend({
  transitions_possibles: z.array(z.object({ code: z.string(), libelle: z.string(), vers: z.string(), motif_obligatoire: z.boolean() })),
  reponses: z.array(z.object({ id: uuid, numero: z.string(), etat: z.string() })),
});

export const schemaListeCourriers = z.object({
  elements: z.array(schemaCourrier),
  suivant: z.string().nullable(),
});
